import { Icon, SkeletonLoader } from '@toggle/design-system';
import { getEntityTagFromSnake, getTickersFromSnakes } from '@toggle/helpers';
import { DetailedCatalystInsight } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { EXPANDED_VALUE } from '~/components/expandable-drawer/ExpandableDrawer';
import { InsightTypePill } from '~/components/insight-type-pill/InsightTypePill';
import { InsightsLivePrice } from '~/components/insights-live-price/InsightsLivePrice';
import { usePriceMarketReactionData } from '~/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { MarketReactionChart } from '~/modules/market-reaction-charts/MarketReactionChart';
import { useEntity } from '~/react-query-hooks/entities/useEntities';
import { useTheme } from '~/react-query-hooks/themes/useThemeOverview';
import { queryKeys } from '~/routes/query-params';
import { useFlags } from '~/stores/use-flags/useFlags';
import { ThemeLogoIcon } from '~/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { EntityHeaderWatchlist } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';

import { MarketCatalystReaction } from '../market-catalyst-reaction/MarketCatalystReaction';
import styles from './CatalystDrawerHeader.module.scss';

export interface CatalystDrawerHeaderProps {
  identifier: string;
  catalystInsight: DetailedCatalystInsight;
  isCompanyCatalyst?: boolean;
}

type MarketWideConfig = {
  iconName: React.ComponentProps<typeof Icon>['iconName'];
  fillColor: string;
};

const getMarketWideCategoryConfig = (
  subCategory: DetailedCatalystInsight['sub_category']
): MarketWideConfig | null => {
  if (subCategory === 'geopolitical_global_events') {
    return {
      iconName: 'Geopolitical',
      fillColor: 'var(--icon-decorative-purple)',
    };
  }

  if (subCategory === 'macroeconomic_policy_changes') {
    return {
      iconName: 'Macroeconomics',
      fillColor: 'var(--text-decorative-teal)',
    };
  }

  return null;
};

export const CatalystDrawerHeader = ({
  identifier,
  catalystInsight,
  isCompanyCatalyst,
}: CatalystDrawerHeaderProps) => {
  const isUniversalDrawerEnabled = useFlags(state =>
    state.getFeatureFlagState('universal_drawer')
  );
  const { t } = useTranslation('insights');
  const companyEntityTag = isCompanyCatalyst ? identifier : undefined;
  const { params } = useQueryParams();
  const isExpanded = params[queryKeys.drawerExpanded] === EXPANDED_VALUE;

  const themeId = !isCompanyCatalyst ? identifier : undefined;
  const entityTag = companyEntityTag
    ? companyEntityTag
    : getEntityTagFromSnake(catalystInsight.snakes[0]);
  const { data: entityData } = useEntity(entityTag);

  const { data: themeData } = useTheme({
    themeId,
  });
  const entity = entityData?.data;
  const isMultipleEntitiesChart = catalystInsight.snakes.length > 1;

  const {
    priceData,
    fromDateEST,
    toDateEST,
    lastProportionChange,
    lastKnownPrice,
    prevCloseData,
    lastKnownDate,
    isLoading: isPriceMarketReactionDataLoading,
  } = usePriceMarketReactionData({
    entityTag,
    ticker: entity?.subscribable_ticker ?? '',
    reportingDate: catalystInsight.created_at,
    enabled: isCompanyCatalyst,
    isMultipleEntitiesChart,
  });

  /* i18next-extract-mark-context-next-line ["financial_operational_updates", "corporate_actions_strategic_announcements", "management_leadership_changes", "legal_regulatory_events"] */
  const translatedSubCategory = t(
    `catalyst.subcategories.${catalystInsight.sub_category}`
  );

  const getThemeHeader = () => {
    if (isCompanyCatalyst && entity) {
      return (
        <div className={styles.entityAndPriceWrapper}>
          <EntityHeaderWatchlist
            entity={entity}
            logoSize={48}
            className={styles.styledEntityHeaderWatchlist}
          />
          <InsightsLivePrice entity={entity} showPricePercentage />
        </div>
      );
    }

    if (themeData) {
      return (
        <div className={styles.iconWrapper}>
          <ThemeLogoIcon logo_url={themeData.logo_url} name={themeData.name} />
          <p className={styles.categoryTitle}>{themeData.name}</p>
        </div>
      );
    }

    const marketWideCategory = getMarketWideCategoryConfig(
      catalystInsight.sub_category
    );

    if (marketWideCategory) {
      return (
        <div className={styles.iconWrapper}>
          <div className={styles.marketWideIconWrapper}>
            <Icon
              iconName={marketWideCategory.iconName}
              size={48}
              fillColor={marketWideCategory.fillColor}
            />
          </div>
          <p className={styles.categoryTitle}>{translatedSubCategory}</p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={styles.root}>
      {!isExpanded && isUniversalDrawerEnabled && getThemeHeader()}
      <InsightTypePill
        createdAt={catalystInsight.created_at}
        subCategory={catalystInsight.sub_category}
        category={catalystInsight.category}
      />
      <div>
        <div className={styles.subCategoryActionTypeWrapper}>
          {themeId ? (
            <div className={styles.titleWrapper}>
              <span>{`${catalystInsight.action_type}${
                !isUniversalDrawerEnabled ? ' · ' : ''
              }`}</span>
              {themeData && !isUniversalDrawerEnabled && (
                <div className={styles.themeTitleWrapper}>
                  <ThemeLogoIcon
                    logo_url={themeData.logo_url}
                    name={themeData.name}
                    size={20}
                  />
                  <div className={styles.themeTitle}>{themeData.name}</div>
                </div>
              )}
            </div>
          ) : (
            `${translatedSubCategory} · ${catalystInsight.action_type}`
          )}
        </div>
        <div className={styles.title}>{catalystInsight.title}</div>
      </div>
      <div className={styles.titleGrid}>
        {!isMultipleEntitiesChart && entity && (
          <div className={styles.watchlistRow}>
            <EntityHeaderWatchlist entity={entity} />
          </div>
        )}
        <div className={styles.chartRow}>
          <ErrorBoundaryWrapper>
            {isPriceMarketReactionDataLoading ? (
              <div className={styles.styledSkeletonLoaderWrapper}>
                <SkeletonLoader
                  areas={[
                    {
                      styles: {
                        width: '100%',
                        height: '400px',
                        borderRadius: '8px',
                      },
                    },
                  ]}
                />
              </div>
            ) : isCompanyCatalyst ? (
              <MarketReactionChart
                fromDateEST={fromDateEST}
                toDateEST={toDateEST}
                lines={
                  priceData
                    ? [
                        {
                          data: priceData,
                          prevCloseData,
                          lastProportionChange,
                          lastKnownPrice,
                          lastKnownDate,
                        },
                      ]
                    : []
                }
                entities={entity ? [entity] : undefined}
              />
            ) : (
              <MarketCatalystReaction
                createdAt={catalystInsight.created_at}
                tags={catalystInsight.snakes.map(getEntityTagFromSnake)}
                tickers={getTickersFromSnakes(catalystInsight.snakes)}
              />
            )}
          </ErrorBoundaryWrapper>
        </div>
      </div>
    </div>
  );
};
